import { ref, Ref } from '@vue/composition-api';
import {
  desktopBreakpoint,
  mobileBreakpoint,
  tabletOnlyBreakpoint,
  tabletUpBreakpoint,
  // @ts-ignore
} from '@fc/angie-ui/dist/styles/_js_exports.scss';

// Create singleton refs at module level
const mobileMatch = ref(false);
const tabletOnlyMatch = ref(false);
const tabletUpMatch = ref(false);
const desktopMatch = ref(false);

// Track if listeners have been initialized
let isInitialized = false;

/**
 * Sets up a media query listener that updates the provided ref when the query matches/unmatches
 */
const setupMediaQuery = (query: string, targetRef: Ref<boolean>) => {
  if (!process.client) return;

  const media = window.matchMedia(query);

  const onChange = () => {
    // eslint-disable-next-line no-param-reassign
    targetRef.value = media.matches;
  };

  // Set initial value
  onChange();

  // Add listener for media query changes
  media.addEventListener('change', onChange);

  // Note: These listeners persist for the app lifetime and don't need cleanup
  // since media query state is global application state
};

/**
 * Initialize all media query listeners once on first call
 */
const initializeMediaListeners = () => {
  if (isInitialized || !process.client) {
    return;
  }

  isInitialized = true;

  setupMediaQuery(mobileBreakpoint, mobileMatch);
  setupMediaQuery(tabletOnlyBreakpoint, tabletOnlyMatch);
  setupMediaQuery(tabletUpBreakpoint, tabletUpMatch);
  setupMediaQuery(desktopBreakpoint, desktopMatch);
};

// Initialize listeners on first import if on client
if (process.client) {
  initializeMediaListeners();
}

// NOTE: This pattern provides a singular point to get reactive media query data
// All components share the same ref instances, so only one listener exists per breakpoint
export const isMobileEffect = () => mobileMatch;
export const isTabletOnlyEffect = () => tabletOnlyMatch;
export const isTabletUpEffect = () => tabletUpMatch;
export const isDesktopEffect = () => desktopMatch;

/**
 * Returns the direct media query ref for use in computed properties or reactive contexts
 * WARNING: When binding directly to template props, this may cause hydration mismatches
 * because SSR always renders with false, but client may be true on first render.
 *
 * For template prop bindings that need to match SSR/client, create a local ref that
 * starts as false and updates in onMounted:
 *
 * @example
 * // In component setup:
 * const isMobile = isMobileEffect();
 * const showNav = ref(false);
 *
 * onMounted(() => {
 *   showNav.value = isMobile.value;
 * });
 *
 * watch(isMobile, (val) => {
 *   showNav.value = val;
 * });
 *
 * // In template:
 * :show-navigation="showNav" // SSR-safe
 */
